import AdsApi from '../../api/ads';
import useSites from '../../hooks/useSites';
import useStatusGroups from '../../hooks/useStatusGroups';
import { siteMeta } from '../connections/sites';
import { EntityFilter, SelectFilter } from '../filters/Filters';
import { StatusDot } from '../status/StatusBadge';

export const DEFAULT_SITE = 'facebook';

// Query kept when moving between the lists (drill down, sidebar)
export const SHARED_KEYS = ['site', 'range', 'since', 'until'];

export function SiteFilter({ query, setQuery }) {
  const sites = useSites();
  const options = sites.map((site) => ({ value: site.site, label: siteMeta(site).name }));
  if (!options.some((option) => option.value === query.site)) options.push({ value: query.site, label: query.site });
  // a site change empties the ad account, campaign and ad set filters, which belong to one site
  return (
    <SelectFilter label="Site" value={query.site} options={options} allLabel={null} width={150}
                  onChange={(site) => setQuery({ site, ad_account: null, campaign: null, adset: null })} />
  );
}

// groups and their colours come from the API (social.constants); type 'ad_account' for ad account statuses
export function StatusFilter({ query, setQuery, type }) {
  const groups = useStatusGroups(type);
  // "All statuses" until the groups have loaded, so the select never holds a value it has no option for
  const value = groups.some((group) => group.value === query.status) ? query.status : '';
  return (
    <SelectFilter label="Status" value={value} allLabel="All statuses" width={200}
                  options={groups.map(({ value: group, label, tone }) => ({
                    value: group, label: <><StatusDot tone={tone} /><span style={{ marginLeft: 8 }}>{label}</span></>,
                  }))}
                  onChange={(status) => setQuery({ status })} />
  );
}

export function AdAccountFilter({ query, setQuery }) {
  return (
    <EntityFilter label="Ad account" value={query.ad_account} fetchList={AdsApi.accounts}
                  params={{ site: query.site }} describe={(account) => account.act_id}
                  onChange={(id) => setQuery({ ad_account: id, campaign: null, adset: null })} />
  );
}

export function CampaignFilter({ query, setQuery }) {
  return (
    <EntityFilter label="Campaign" value={query.campaign} fetchList={AdsApi.campaigns}
                  params={{ site: query.site, ad_account: query.ad_account }}
                  describe={(campaign) => campaign.ad_account_name}
                  onChange={(id) => setQuery({ campaign: id, adset: null })} />
  );
}

export function AdSetFilter({ query, setQuery }) {
  return (
    <EntityFilter label="Ad set" value={query.adset} fetchList={AdsApi.adsets}
                  params={{ site: query.site, ad_account: query.ad_account, campaign: query.campaign }}
                  describe={(adset) => adset.campaign_name} onChange={(id) => setQuery({ adset: id })} />
  );
}

// Link to another list with the shared query plus extra filters
export function listUrl(path, query, filters) {
  const params = new URLSearchParams();
  SHARED_KEYS.forEach((key) => {
    const isDefault = key === 'site' && query[key] === DEFAULT_SITE;
    if (query[key] && !isDefault) params.set(key, query[key]);
  });
  Object.entries(filters).forEach(([key, value]) => value != null && params.set(key, value));
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}
